import React from 'react';
import { formatTaskType, formatTime, getDuration } from './utils';

const t = {
  delay: 'Possible delay:',
  delay_check: 'Delay check',
  risk_of_delay: 'Risk of delay',
  late: 'Late:',
  not_started: 'Not started:',
  standstill: 'Standstill:',
  open_chat: 'Unanswered',
  message: 'message',
  xdock_incident: 'Load incident',
  from: 'from',
  no_gps: 'No GPS',
  no_app: 'No app',
  possible_standstill: 'Possible standstill',
  started_too_early: 'Started too early',
  review: 'Review',
};

const DelayDescription = React.memo(({ eta, timeTo, comment }) => {
  const duration = !!eta && !!timeTo && getDuration({ from: timeTo, to: eta });

  return (
    <>
      <span className="alert-urgent">{t.delay}</span>
      {duration && formatTime(duration)} {comment}
    </>
  );
});

const LateDescription = React.memo(({ timeTo, comment }) => {
  const duration = !!timeTo && getDuration({ from: timeTo, to: new Date() });

  return (
    <>
      <span className="alert-urgent">{t.late}</span>
      {duration && formatTime(duration)} {comment}
    </>
  );
});

const StandstillDescription = React.memo(({ comment, arrivedTime }) => {
  const duration = !!arrivedTime && getDuration({ from: arrivedTime, to: new Date() });

  return (
    <>
      <span className="alert-moderate">{t.standstill}</span>
      {duration && formatTime(duration)} {comment}
    </>
  );
});

const NotStartedDescription = React.memo(({ comment }) => (
  <>
    <span className="alert-moderate">{t.not_started}</span>
    {comment}
  </>
));

const OpenChatDescription = React.memo(({ driverName }) => (
  <>
    <span className="alert-urgent">{t.open_chat}</span>
    <strong className="margin-right">{t.message}</strong>
    {driverName && (
      <>
        {t.from} {driverName}
      </>
    )}
  </>
));

const DelayCheckDescription = React.memo(({ comment }) => {
  return (
    <>
      <strong className="margin-right">{t.no_gps} -</strong>
      <span className="alert-moderate">{t.delay_check}</span>
      {comment}
    </>
  );
});

const RiskOfDelayDescription = React.memo(({ comment }) => {
  return (
    <>
      <span className="alert-urgent">{t.risk_of_delay}</span>
      {comment}
    </>
  );
});

const PossibleStandstillDescription = React.memo(({ comment }) => {
  return (
    <>
      <strong className="margin-right">{t.no_app} -</strong>
      <span className="alert-moderate">{t.possible_standstill}</span>
      {comment}
    </>
  );
});

const XDockIncidentDescription = React.memo(({ comment }) => {
  return (
    <>
      <span className="alert-urgent margin-right">{t.xdock_incident}</span>
      <span>{comment}</span>
    </>
  );
});

const EarlyStartDescription = React.memo(({ comment }) => {
  return (
    <>
      <span className="alert-moderate">{t.started_too_early}</span>
      <span>{comment}</span>
    </>
  );
});

const ReviewDescription = React.memo(({ comment }) => {
  return (
    <>
      <span className="alert-moderate">{t.review}</span>- {comment}
    </>
  );
});

const ManualDescription = React.memo(({ comment }) => <>{comment}</>);

const taskDescriptionTypes = {
  MANUAL: ({ comment }) => <ManualDescription comment={comment} />,
  LATE: ({ time_to, comment }) => <LateDescription timeTo={time_to} comment={comment} />,
  NOT_STARTED: ({ comment }) => <NotStartedDescription comment={comment} />,
  OPEN_CHAT: ({ driver_name }) => <OpenChatDescription driverName={driver_name} />,
  DELAY_RISK: ({ comment }) => <RiskOfDelayDescription comment={comment} />,
  DELAY_NO_GPS: ({ comment }) => <DelayCheckDescription comment={comment} />,
  DELAY: ({ eta, time_to, comment }) => (
    <DelayDescription eta={eta} timeTo={time_to} comment={comment} />
  ),
  STANDSTILL: ({ comment, arrived_time }) => (
    <StandstillDescription arrivedTime={arrived_time} comment={comment} />
  ),
  POSSIBLE_STANDSTILL: ({ comment }) => <PossibleStandstillDescription comment={comment} />,
  XDOCK_INCIDENT: (props) => <XDockIncidentDescription {...props} />,
  SCANLOG: ({ comment }) => <ManualDescription comment={comment} />,
  EARLY_START: ({ comment, early_mins }) => (
    <EarlyStartDescription comment={comment} earlyMins={early_mins} />
  ),
  REVIEW_JOURNEY_AGGREGATION: ({ comment }) => <ReviewDescription comment={comment} />,
};

const TaskDescription = ({ className, comment, commentData, taskType }) => {
  const taskTypeFormatted = formatTaskType(taskType);

  if (!taskDescriptionTypes[taskTypeFormatted]) {
    return taskTypeFormatted;
  }

  const descriptionComponent = taskDescriptionTypes[taskTypeFormatted]({
    ...commentData,
    comment,
  });

  return <div className={className}>{descriptionComponent}</div>;
};

export default TaskDescription;
